import * as React from 'react'
import { Dispatch, SetStateAction } from 'react'
import { toast } from 'react-toastify'

import useGoogleClient from '@/pages/Settings/Integrations/components/useGoogleClient'

import API from '../api'

export type GoogleUserDetails = {
  access_token: string
  refresh_token: string
  email: string
  team: number
  status: 'connected' | 'disconnected'
}

export type IntegrationsContextType = {
  googleIntegrationActive: boolean
  isLoadingDisconnect: boolean
  isLoadingOAuth: boolean
  googleUserDetails?: GoogleUserDetails
  setGoogleUserDetails?: Dispatch<SetStateAction<GoogleUserDetails | undefined>>
  setGoogleIntegrationActive: (active: boolean) => void
  getGoogleIntegration: () => Promise<void>
  onDisconnect: () => Promise<void>
  oauthSignIn: () => void
}

export const IntegrationsContext = React.createContext<IntegrationsContextType>(
  {} as IntegrationsContextType
)

function IntegrationsContextProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [googleUserDetails, setGoogleUserDetails] = React.useState<
    GoogleUserDetails | undefined
  >()
  const [googleIntegrationActive, setGoogleIntegrationActive] =
    React.useState(false)
  const [isLoadingDisconnect, setIsLoadingDisconnect] = React.useState(false)

  const getGoogleIntegration = async () => {
    try {
      const response = (await API.getGoogleIntegration()) as GoogleUserDetails
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (response.access_token) {
        setGoogleIntegrationActive(true)
        setGoogleUserDetails(response)
      }
    } catch (err) {
      setGoogleIntegrationActive(false)
      setGoogleUserDetails(undefined)
    }
  }

  const { tokenClient, isLoadingOAuth } = useGoogleClient({
    setGoogleIntegrationActive,
    onGoogleConnected: getGoogleIntegration,
  })

  const oauthSignIn = React.useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    tokenClient?.requestCode()
  }, [tokenClient])

  const onDisconnect = async () => {
    setIsLoadingDisconnect(true)
    try {
      await API.deleteGoogleIntegration()
      toast.success('Google integration disconnected')
      setGoogleIntegrationActive(false)
      setGoogleUserDetails(undefined)
    } catch (error) {
      toast.error('Error trying connect with Google')
    } finally {
      setIsLoadingDisconnect(false)
    }
  }

  return (
    <IntegrationsContext.Provider
      value={{
        isLoadingOAuth,
        isLoadingDisconnect,
        oauthSignIn,
        onDisconnect,
        googleUserDetails,
        setGoogleUserDetails,
        googleIntegrationActive,
        setGoogleIntegrationActive,
        getGoogleIntegration,
      }}
    >
      {children}
    </IntegrationsContext.Provider>
  )
}

export default IntegrationsContextProvider
